import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase, type Item } from '../lib/supabase'
import { purchasesFrom } from '../lib/restock'
import { buildCategoryMemory } from '../lib/autocategory'
import { buildCatalog, buildSpellings, historyGroups, indexNames, spellingFor } from '../lib/catalog'
import { pastTrips } from '../lib/trips'

type Row = Pick<Item, 'id' | 'done_by' | 'name' | 'category_id' | 'quantity' | 'done' | 'done_at' | 'deleted_at' | 'updated_at' | 'created_by' | 'created_at'>

// Newest first; enough for years of a family's trips, and every derived view
// below only needs a few rows per name.
const LIMIT = 2000
// Running low looks back this far; older purchases say nothing about now.
const PURCHASE_WINDOW_DAYS = 180

export const itemHistoryKey = (householdId: string) => ['item-history', householdId] as const

/**
 * The household's past rows — everything Finish trip or a delete soft-deleted
 * (`useItems` holds only the current list) — fetched once and read three ways:
 *
 * - `purchases`: ticked-and-cleared rows, for the Running low chips (lib/restock.ts)
 * - `memory`: nameKey → category, for quick add and recipe import (lib/autocategory.ts)
 * - `catalog`: every name used before and not on the list, for quick add (lib/catalog.ts)
 * - `spellingFor`: the household's own name for an ingredient, for recipe adds (lib/catalog.ts)
 * - `trips`: past trips, for the trip summary's "than usual" (lib/trips.ts)
 * - `rows`: the rows themselves, for the stats page (lib/stats.ts)
 */
export function useItemHistory(householdId: string, liveItems: Item[]) {
  const { data, dataUpdatedAt } = useQuery({
    queryKey: itemHistoryKey(householdId),
    enabled: !!householdId,
    // Grows only when a trip is finished or an item deleted; Finish trip refetches it.
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('items').select('id, name, category_id, quantity, done, done_at, done_by, deleted_at, updated_at, created_by, created_at')
        .eq('household_id', householdId)
        .not('deleted_at', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(LIMIT)
      if (error) throw error
      return data as Row[]
    },
  })

  const past = data
  const purchases = useMemo(() => {
    const since = dataUpdatedAt - PURCHASE_WINDOW_DAYS * 86_400_000
    return purchasesFrom(past ?? []).filter(p => p.doneAt.getTime() >= since)
  }, [past, dataUpdatedAt])
  // Live items last so an in-flight recategorisation beats the fetched copy.
  const memory = useMemo(() => buildCategoryMemory([...(past ?? []), ...liveItems]), [past, liveItems])
  const catalog = useMemo(() => buildCatalog(past ?? [], liveItems), [past, liveItems])
  const names = useMemo(() => indexNames(buildSpellings([...(past ?? []), ...liveItems]).values()), [past, liveItems])
  const spelling = useCallback((name: string) => spellingFor(name, names), [names])
  const groups = useMemo(() => historyGroups(past ?? []), [past])
  const trips = useMemo(() => pastTrips(past ?? []), [past])
  return { purchases, memory, catalog, spellingFor: spelling, groups, trips, rows: past ?? [], loaded: !!past }
}

export type ForgetResult = { ok: true; count: number } | { ok: false; error: 'failed' }

/**
 * Settings › Item history: hard-deletes past rows by exact name, or the whole
 * history with null (forget_items, 034_forget_items.sql). Never touches the
 * live list. The history query is refetched after, so From before, Running
 * low and the category memory forget them at once on this device.
 */
export function useForgetItems(householdId: string) {
  const qc = useQueryClient()
  return useCallback(async (names: string[] | null): Promise<ForgetResult> => {
    const { data, error } = await supabase.rpc('forget_items', { p_names: names })
    if (error) return { ok: false, error: 'failed' }
    await qc.invalidateQueries({ queryKey: itemHistoryKey(householdId) })
    return { ok: true, count: (data as number | null) ?? 0 }
  }, [qc, householdId])
}
